/* Smooth-buffer video engine: feeds a <video> through Media Source Extensions from the server's fragmented-MP4 remux,
   keeping a configurable number of seconds buffered ahead and fetching only when the buffer runs low.
   Any failure calls onFallback(position) so the page can switch to the plain progressive stream. */

const MP4_AUDIO = { aac: "mp4a.40.2", mp3: "mp4a.6B" };
export const mseMime = (v) => {
  if (v.ext === "webm") return `video/webm; codecs="${v.vcodec === "av1" ? "av01.0.08M.08" : v.vcodec || "vp9"}${v.acodec ? `, ${v.acodec}` : ""}"`;
  return `video/mp4; codecs="avc1.42E01E${v.acodec && MP4_AUDIO[v.acodec] ? `, ${MP4_AUDIO[v.acodec]}` : ""}"`;
};
export const mseSupported = (v) => !!(window.MediaSource && v?.mse && window.MediaSource.isTypeSupported(mseMime(v)));

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const rangeAt = (buffered, t) => {
  for (let i = 0; i < buffered.length; i++) if (t >= buffered.start(i) - 0.3 && t <= buffered.end(i)) return [buffered.start(i), buffered.end(i)];
  return null;
};

export function attachMse(el, { url, duration, mime, ahead, start = 0, onFallback, onLog }) {
  const ms = new MediaSource();
  let sb = null, ctrl = null, dead = false, loading = 0, lastSeekLoad = -1;
  const getAhead = typeof ahead === "function" ? ahead : () => ahead;
  const log = (m) => onLog?.(m);

  const fail = (why) => {
    if (dead) return;
    log(`fallback: ${why}`);
    const pos = el.currentTime;
    cleanup();
    onFallback?.(pos, why);
  };

  const append = (chunk) => new Promise((resolve, reject) => {
    const done = () => { sb.removeEventListener("updateend", done); sb.removeEventListener("error", err); resolve(); };
    const err = () => { sb.removeEventListener("updateend", done); sb.removeEventListener("error", err); reject(new Error("append error")); };
    sb.addEventListener("updateend", done); sb.addEventListener("error", err);
    try { sb.appendBuffer(chunk); } catch (e) { sb.removeEventListener("updateend", done); sb.removeEventListener("error", err); reject(e); }
  });
  const remove = (a, b) => new Promise((resolve) => { const done = () => { sb.removeEventListener("updateend", done); resolve(); }; sb.addEventListener("updateend", done); sb.remove(a, b); });
  const waitIdle = async () => { while (sb && sb.updating) await sleep(20); };

  const load = async (from) => {
    if (dead || !sb) return;
    const my = ++loading;
    ctrl?.abort();
    ctrl = new AbortController();
    await waitIdle();
    if (dead || my !== loading) return;
    log(`load from ${from.toFixed(1)}`);
    let r;
    try { r = await fetch(`${url}${url.includes("?") ? "&" : "?"}start=${Math.max(0, from).toFixed(3)}`, { signal: ctrl.signal, credentials: "include" }); }
    catch (e) { if (e.name !== "AbortError") fail(`fetch: ${e.message}`); return; }
    if (my !== loading) return;
    if (!r.ok || !r.body) { fail(`HTTP ${r.status}`); return; }
    const reader = r.body.getReader();
    try {
      while (!dead && my === loading) {
        const rg = rangeAt(el.buffered, el.currentTime);
        const have = rg ? rg[1] - el.currentTime : 0;
        if (rg && have > getAhead()) { await sleep(500); continue; } // enough buffered → let the connection idle
        const { value, done } = await reader.read();
        if (done) { if (!dead && my === loading && ms.readyState === "open") { await waitIdle(); try { ms.endOfStream(); } catch { /* already ended */ } } return; }
        await waitIdle();
        if (dead || my !== loading) return;
        try { await append(value); }
        catch (e) {
          if (e.name === "QuotaExceededError" && el.currentTime > 40) { await remove(0, el.currentTime - 30); await waitIdle(); await append(value); }
          else throw e;
        }
      }
    } catch (e) {
      if (e.name !== "AbortError" && my === loading) fail(`append: ${e.message}`);
    } finally {
      try { reader.cancel(); } catch { /* ignore */ }
    }
  };

  const onSeeking = () => {
    const t = el.currentTime;
    const rg = rangeAt(el.buffered, t);
    if (rg && rg[1] - t > 0.5) return; // already buffered
    if (Math.abs(t - lastSeekLoad) < 0.25) return;
    lastSeekLoad = t;
    load(t);
  };

  const onOpen = () => {
    try {
      ms.duration = duration;
      sb = ms.addSourceBuffer(mime);
      sb.mode = "segments";
      sb.addEventListener("error", () => fail("sourcebuffer error"));
    } catch (e) { fail(`open: ${e.message}`); return; }
    el.addEventListener("seeking", onSeeking);
    lastSeekLoad = start;
    if (start > 0) el.currentTime = start;
    load(start);
  };
  ms.addEventListener("sourceopen", onOpen, { once: true });
  const objectUrl = URL.createObjectURL(ms);
  el.src = objectUrl;

  function cleanup() {
    dead = true;
    loading++;
    ctrl?.abort();
    el.removeEventListener("seeking", onSeeking);
    try { if (ms.readyState === "open") ms.endOfStream(); } catch { /* ignore */ }
    URL.revokeObjectURL(objectUrl);
  }
  return cleanup;
}

/* Seconds buffered ahead of the playhead, for the on-screen indicator. */
export const bufferedAhead = (el) => { if (!el) return 0; const rg = rangeAt(el.buffered, el.currentTime); return rg ? Math.max(0, rg[1] - el.currentTime) : 0; };
